import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { beautifyHTML } from "123tcs-common/utils/beautify";
import React, { useEffect, useState } from "react";
import AceEditor from "react-ace";
import { renderToStaticMarkup, renderToString } from "react-dom/server";
import { SIMULATION_CARD } from "../../constants/simulation";
import {
  EditorSettingsProvider,
  useEditorSettings,
} from "../../contexts/EditorSettings";
import { LanguageProvider, useLanguage } from "../../contexts/Language";
import { SimulationProvider } from "../../contexts/Simulation";
import BalanceChecker from "../balance-checker/BalanceChecker";
import Card from "../balance-checker/Card";
import Transactions from "../balance-checker/Transactions";

import "ace-builds/src-noconflict/mode-html";

function BalanceCheckerHTMLViewer() {
  // Contexts
  const { settings, saveSettings } = useEditorSettings();
  const { languageCode } = useLanguage();

  // Constants
  const getFormHTML = () => {
    return renderToString(
      <LanguageProvider>
        <EditorSettingsProvider>
          <SimulationProvider simulateEditorSettings>
            <BalanceChecker />
          </SimulationProvider>
        </EditorSettingsProvider>
      </LanguageProvider>,
    );
  };

  const getResultHTML = () => {
    return renderToStaticMarkup(
      <LanguageProvider>
        <EditorSettingsProvider>
          <SimulationProvider>
            <Card card={SIMULATION_CARD} />
            <Transactions card={SIMULATION_CARD} showEndOfLife />
          </SimulationProvider>
        </EditorSettingsProvider>
      </LanguageProvider>,
    );
  };

  // States
  const [value, setValue] = useState(getFormHTML());
  const [htmlPage, setHTMLPage] = useState("form");

  // Functions
  const handleChange = (event, newHTMLPage) => {
    if (newHTMLPage !== null) {
      setHTMLPage(newHTMLPage);
    }
  };

  const handleShow = () => {
    saveSettings({
      ...settings,
      showHTML: !settings.showHTML,
    });
  };

  // Effects
  useEffect(() => {
    switch (htmlPage) {
      case "form":
        setValue(getFormHTML());
        break;
      case "result":
        setValue(getResultHTML());
        break;
      default:
        setValue("form");
    }
  }, [htmlPage]);

  useEffect(() => {
    setValue(getFormHTML());
  }, [settings, languageCode]);

  // Render
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: settings.showHTML ? 1 : 0,
        // display: "flex",
        // alignItems: "center",
        // justifyContent: "center",
      }}
    >
      <Box
        display="flex"
        sx={{
          padding: 1,
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          HTML
        </Typography>

        <Stack direction="row" spacing={1}>
          <Tooltip title="HTML Pages">
            <ToggleButtonGroup
              color="primary"
              value={htmlPage}
              exclusive
              onChange={handleChange}
              size="small"
            >
              <ToggleButton value="form">Form</ToggleButton>
              <ToggleButton value="result">Result</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>

          <Tooltip title="Hide">
            <IconButton onClick={handleShow}>
              {settings.showHTML ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={settings.showHTML} unmountOnExit>
        {settings.showHTML && (
          <AceEditor
            mode="html"
            // (v. height - header - settings - padding) / 2 - HTML header - margin
            height="calc((100vh - 66px - 77px - 32px) / 2 - 56px - 8px)"
            width="100%"
            value={beautifyHTML(value)}
            readOnly
            wrapEnabled
            showPrintMargin={false}
            setOptions={{
              enableKeyboardAccessibility: true,
              useWorker: false,
            }}
          />
        )}
      </Collapse>
    </Paper>
  );
}

export default BalanceCheckerHTMLViewer;
